import { ITransfer } from "types/MoneyTransfers";
import { IOrder } from "types/Order/Order";

export const mapFormToRequestBody = ({
  orderId,
  moneyTransfers,
  note,
}: {
  orderId: string;
  moneyTransfers: ITransfer[];
  note?: string;
}) => {
  return {
    orderId,
    note,
    moneyTransfers: moneyTransfers
      .filter(
        (transfer) =>
          transfer.selectedPartyToTransferFrom &&
          transfer.selectedPartyToTransferTo &&
          transfer.selectedAmountToTransfer > 0
      )
      .map((transfer) => ({
        fromProviderId: transfer?.selectedPartyToTransferFrom?.id,
        toProviderId: transfer?.selectedPartyToTransferTo?.id,
        amountInCents: transfer.selectedAmountToTransfer,
        withholdClearFee: transfer.withholdClearFee,
        // CLE-2458: only send the chosen refund card when the destination is the patient (a refund).
        ...(transfer.selectedPartyToTransferTo?.type === "patient" &&
        transfer.selectedRefundCardPaymentId
          ? { sourcePaymentId: transfer.selectedRefundCardPaymentId }
          : {}),
      })),
  };
};

// CLE-2473 / CLE-2863: a Finix transfer row with `returnedAt` set never moved money — an ACH that
// came back RETURNED, or a reversal (patient refund) Finix accepted and then declined. It must not
// count toward what a provider holds. The backend's getFinixTransfersStateByProviderId skips these
// rows the same way, so the dialog's caps match what the server will allow.
const finixTransferMoved = (transfer: { returnedAt?: string | null }) =>
  !transfer.returnedAt;

export interface IProviderTotal {
  name: string;
  id: string;
  totalAmount: number;
  type: string;
}

/**
 * Per-provider amount the "From" dropdown offers and caps the amount at: what each provider still
 * holds on this order (transfers in, net of reversals out, plus the Clear fee withheld).
 */
export const buildProviderTotals = ({
  providerStripeTransfers,
  providerFinixTransfers,
}: {
  providerStripeTransfers?: IOrder["providerStripeTransfers"];
  providerFinixTransfers?: IOrder["providerFinixTransfers"];
}): IProviderTotal[] => {
  const totals: { [key: string]: IProviderTotal } = {};
  // Aggregate from Stripe transfers
  providerStripeTransfers?.forEach((transfer) => {
    if (!totals[transfer.providerId]) {
      totals[transfer.providerId] = {
        name: transfer.provider.name,
        id: transfer.providerId,
        totalAmount: 0,
        type: "provider",
      };
    }
    totals[transfer.providerId].totalAmount +=
      transfer.amountInCents + transfer.clearFeeAmountInCents;
  });
  // Aggregate from Finix transfers. Skip CLEARHEALTH_UPCHARGE rows — those have a
  // null providerId/provider and represent the Clear Health merchant's split, not a
  // refundable provider pot. Backend mirrors this in getFinixTransfersStateByProviderId.
  providerFinixTransfers?.forEach((transfer: any) => {
    if (!transfer.providerId || !transfer.provider) return;
    if (!finixTransferMoved(transfer)) return;
    if (!totals[transfer.providerId]) {
      totals[transfer.providerId] = {
        name: transfer.provider.name,
        id: transfer.providerId,
        totalAmount: 0,
        type: "provider",
      };
    }
    totals[transfer.providerId].totalAmount +=
      transfer.amountInCents + (transfer.clearFeeAmountInCents || 0);
  });
  return Object.values(totals);
};

// CLE-2458: a patient refund reverses the chosen provider's transfers, and a split-paid card's
// money is spread across providers. So the real cap for "refund $X to card C from provider P" is
// how much of card C's money provider P still holds — NOT card C's full charge. We compute that
// (provider ∩ card) reversible from the order's provider transfers; reversal rows are negative and
// carry the same providerId+paymentId, so summing nets out anything already refunded. The backend
// enforces the same limit before any reversal, so this is a UX guard, not the source of truth.
export const reversibleForProviderCardInCents = ({
  order,
  providerId,
  cardPaymentId,
}: {
  order: IOrder;
  providerId?: string | null;
  cardPaymentId?: string;
}): number | undefined => {
  if (!providerId || !cardPaymentId) return undefined;
  const finixRows: any[] = (order.providerFinixTransfers || []).filter(
    (t: any) =>
      t.providerId === providerId &&
      t.paymentId === cardPaymentId &&
      finixTransferMoved(t)
  );
  const stripeRows: any[] = (order.providerStripeTransfers || []).filter(
    (t: any) => t.providerId === providerId && t.paymentId === cardPaymentId
  );
  const rows: any[] = finixRows.length ? finixRows : stripeRows;
  const net = rows.reduce(
    (sum: number, t: any) =>
      sum + t.amountInCents + (t.clearFeeAmountInCents || 0),
    0
  );
  return Math.max(0, net);
};

// CLE-2997: only a card SALE can be refunded to the patient's card. Mirrors the backend rule in
// finix_money_transfers.helpers.ts (isFinixCardSaleLeg / getFinixTransfersStateByProviderId).
const isCardSaleLeg = (t: any) =>
  !t.isMoneyTransfer && t.amountInCents > 0 && !t.originalTransferId;

// A real card reversal of `legId` (patient refund or cancellation). Provider-to-provider and
// provider-to-Clear debits are linked to the sale too but never touched the card, so they don't count.
const isCardReversalOf = (t: any, legId: string) =>
  t.reversedCardSaleTransferId
    ? t.reversedCardSaleTransferId === legId
    : !t.isMoneyTransfer && t.originalTransferId === legId;

const grossOf = (t: any) =>
  Math.abs(t.amountInCents) +
  Math.abs(t.clearFeeAmountInCents || 0) +
  Math.abs(t.clearPatientFeeAmountInCents || 0);

export interface IFinixCardRefund {
  /** What the provider holds on the order (the "From" dropdown's figure, card-scoped when pinned). */
  balanceInCents: number;
  /** How much of that can go back to the patient's card: min(balance, card-sale headroom). */
  refundableInCents: number;
  /** Other providers on the order that still have card money Finix can refund (the merchants of record). */
  merchantsOfRecord: string[];
}

/**
 * CLE-2997: how much a Finix patient refund from `providerId` can send back to the card. Money a provider
 * received by provider-to-provider or Clear-to-provider transfer is part of its balance but can't be
 * reversed to the card; the backend refuses such a refund before any Finix call, so the dialog caps the
 * amount here and names who to transfer to first. A card-sale leg's headroom is its split on the sale
 * (net + clear fee + clear patient fee) minus the real card reversals against it.
 */
export const finixCardRefundForProvider = ({
  order,
  providerId,
  cardPaymentId,
}: {
  order: IOrder;
  providerId: string;
  cardPaymentId?: string;
}): IFinixCardRefund => {
  const rows: any[] = (order.providerFinixTransfers || []).filter(
    (t: any) =>
      t.providerId &&
      t.transferRole !== "CLEARHEALTH_UPCHARGE" &&
      finixTransferMoved(t)
  );
  const headroomOf = (pid: string) =>
    rows
      .filter(
        (t) =>
          t.providerId === pid &&
          isCardSaleLeg(t) &&
          (!cardPaymentId || t.paymentId === cardPaymentId)
      )
      .reduce((sum, leg) => {
        const reversed = rows
          .filter((t) => t.providerId === pid && isCardReversalOf(t, leg.id))
          .reduce((s, t) => s + grossOf(t), 0);
        return (
          sum +
          Math.max(
            0,
            leg.amountInCents +
              (leg.clearFeeAmountInCents || 0) +
              (leg.clearPatientFeeAmountInCents || 0) -
              reversed
          )
        );
      }, 0);

  const balanceInCents = cardPaymentId
    ? reversibleForProviderCardInCents({ order, providerId, cardPaymentId }) ??
      0
    : Math.max(
        0,
        rows
          .filter((t) => t.providerId === providerId)
          .reduce(
            (sum, t) => sum + t.amountInCents + (t.clearFeeAmountInCents || 0),
            0
          )
      );

  const merchantsOfRecord: string[] = [];
  const seen = new Set<string>();
  rows.forEach((t) => {
    if (t.providerId === providerId || seen.has(t.providerId)) return;
    seen.add(t.providerId);
    if (headroomOf(t.providerId) > 0)
      merchantsOfRecord.push(t.provider?.name || "another provider");
  });

  return {
    balanceInCents,
    refundableInCents: Math.min(balanceInCents, headroomOf(providerId)),
    merchantsOfRecord,
  };
};

/**
 * CLE-2997: the dialog's explanation when a refund from `providerName` can't all go back to the card.
 * Same wording as the backend's refusal.
 */
export const describeFinixCardRefundLimit = (
  providerName: string,
  { refundableInCents, merchantsOfRecord }: IFinixCardRefund
): string => {
  const merchants = merchantsOfRecord.join(" or ");
  if (refundableInCents <= 0) {
    return `${providerName}'s money on this order came from a transfer, not the patient's card. ${
      merchants
        ? `Transfer it to ${merchants} first, then refund from ${merchants}.`
        : "No provider on this order has card money left to refund to the patient."
    }`;
  }
  const max = `$${(refundableInCents / 100).toFixed(2)}`;
  return `Only ${max} can go back to the patient's card from ${providerName}; the rest of its money on this order came from a transfer, not the patient's card.${
    merchants
      ? ` Transfer the rest to ${merchants} to refund it from there.`
      : ""
  }`;
};
